import API from '@/api/api';
import {computed, type ComputedRef, ref} from 'vue';
import {computedAsync, until} from '@vueuse/core';
import {defineStore, storeToRefs} from 'pinia';
import {useUserStore} from './userStore';
import {parseZodSchema} from '@/api/models/parseZodSchema';
import {
    featureFlagModel,
    featureFlagResponseModel,
    type FeatureFlagResponse,
    type LicensedFeatureFlag,
    type ValidLicense,
} from '@/api/models/featureFlags/featureFlagModel';
import type {FeatureFlagName} from '@/api/models/featureFlags/featureFlagName';

export const useFeatureFlagStore = defineStore('featureFlags', () => {
    const {useFeatureFlag} = useHierarchicalFeatureFlags();

    const optimizedSeatDayBooking = useFeatureFlagCall('/api/flag/optimized-seat-day-booking');
    const sendNotifications = useFeatureFlagCall('/api/flag/send-notifications', true);
    const receiveNotifications = useFeatureFlagCall('/api/flag/receive-notifications');

    //booking flags
    const newBooking = useFeatureFlag('new-booking');
    const bookingFilter = useFeatureFlag('booking-filter');
    const bookingPresentUsers = useFeatureFlag('booking-present-users');
    const bookingResourceList = useFeatureFlag('booking-resource-list');
    const bookingResourceListStreaming = useFeatureFlag('booking-resource-list-streaming');
    const bookingDetailPage = useFeatureFlag('booking-detail-page');
    const bookingQueue = useFeatureFlag('booking-queue');
    const bookingCalendarCollapse = useFeatureFlag('booking-calendar-collapse');
    const bookingComments = useFeatureFlag('booking-comments');
    const bookingSpaceAvailabilities = useFeatureFlag('booking-space-availabilities');

    const newNavigation = useFeatureFlag('new-booking');
    const kioskMode = useFeatureFlagCall('/api/flag/kiosk-mode');
    const kioskModeCheckIn = useFeatureFlag('kiosk-mode-check-in');
    const calendarUserSubscriptions = useFeatureFlagCall('/api/flag/calendar-user-subscriptions');
    const outlookCalendarSync = useFeatureFlagCall('/api/flag/calendar-sync', true);
    const googleSync = useFeatureFlagCall('/api/flag/google-sync', true);
    const restApi = useFeatureFlagCall('/api/flag/rest-api', true);
    const desklyConnect = useFeatureFlag('deskly-connect');
    const desklyConnectAutoBooking = useFeatureFlag('deskly-connect-auto-booking');
    const desklyConnectLiveStatus = useFeatureFlag('deskly-connect-live-status');
    const desklyConnectUsageStatistics = useFeatureFlag('deskly-connect-usage-statistics');
    const auditLogAccess = useFeatureFlagCall('/api/flag/audit-log-access', true);
    const weekOverview = useFeatureFlagCall('/api/flag/week-overview', true);
    const celloReferralAccess = useFeatureFlagCall('/api/flag/cello', true);
    const occupancyAccess = useFeatureFlag('occupancy');
    const bookingRuleRestriction = useFeatureFlag('booking-rule-restrictions');
    const bookingRulePriority = useFeatureFlag('booking-rule-priorities');
    const bookingRulePermanent = useFeatureFlag('booking-rule-permanent');
    const bookingRules = useCombinedAnyFeatureFlags(
        bookingRulePriority,
        bookingRulePermanent,
        bookingRuleRestriction
    );
    const markerSizing = useFeatureFlag('marker-sizing');
    const spaceImages = useFeatureFlag('space-images');
    const parkingSpaceResource = useFeatureFlag('parking-spaces');
    const aiAssistant = useFeatureFlag('ai-assistant');
    const licensePlates = useFeatureFlag('license-plates');
    const userBookingSummary = useFeatureFlag('user-booking-summary');
    const userTags = useFeatureFlag('user-tags');
    const userTagsCustom = useFeatureFlag('user-tags-custom');
    const resourceConversion = useFeatureFlag('resource-conversion');
    const cancelledBooking = useFeatureFlag('cancelled-bookings');
    const licenseSelfService = useFeatureFlag('license-self-service');
    const customTimeframes = useFeatureFlag('custom-timeframes');
    const newUserAdministration = useFeatureFlag('new-user-administration');
    const suppressionListRemoval = useFeatureFlag('suppression-list-removal');
    const selectionInfoBanner = useFeatureFlag('selection-info-banner');
    const analyticsSensorStats = useFeatureFlag('deskly-connect-analytics-sensor-stats');
    const occupancySensorStats = useFeatureFlag('deskly-connect-occupancy-sensor-stats');
    const separatedBookingRules = useFeatureFlag('separated-booking-rules');
    const permissionGroupCheckin = useFeatureFlag('permission-group-checkin');

    return {
        optimizedSeatDayBooking,
        sendNotifications,
        receiveNotifications,

        //booking flags
        newBooking,
        bookingFilter,
        bookingPresentUsers,
        bookingResourceList,
        bookingResourceListStreaming,
        bookingDetailPage,
        bookingQueue,
        bookingCalendarCollapse,
        bookingSpaceAvailabilities,

        newNavigation,
        kioskMode,
        kioskModeCheckIn,
        calendarUserSubscriptions,
        outlookCalendarSync,
        restApi,
        desklyConnect,
        desklyConnectAutoBooking,
        desklyConnectLiveStatus,
        desklyConnectUsageStatistics,
        auditLogAccess,
        weekOverview,
        celloReferralAccess,
        googleSync,
        occupancyAccess,
        bookingRules,
        bookingRuleRestriction,
        bookingRulePriority,
        bookingRulePermanent,
        markerSizing,
        spaceImages,
        parkingSpaceResource,
        aiAssistant,
        licensePlates,
        userBookingSummary,
        bookingComments,
        userTags,
        userTagsCustom,
        resourceConversion,
        cancelledBooking,
        licenseSelfService,
        newUserAdministration,
        customTimeframes,
        selectionInfoBanner,
        suppressionListRemoval,
        analyticsSensorStats,
        occupancySensorStats,
        separatedBookingRules,
        permissionGroupCheckin,
    };
});

export type FeatureFlagState = {
    loading: boolean;
    isActive: boolean;
    isReady: () => Promise<boolean>;
    availableInLicenseTiers: ValidLicense[] | null;
};

/**
 * @deprecated use `useFeatureFlag` from {@link useHierarchicalFeatureFlags} instead
 */
function useFeatureFlagCall(endpoint: string, lazy = false): ComputedRef<FeatureFlagState> {
    const userStore = useUserStore();

    const loading = ref(false);
    const isActive = computedAsync(
        async () => {
            if (!userStore.isAuthenticated || !userStore.user?.id) {
                return false;
            }

            return await API.get(endpoint)
                .then(API.handle)
                .then(data => parseZodSchema(data, featureFlagModel).enabled)
                .catch(() => false);
        },
        null,
        {
            lazy,
            evaluating: loading,
        }
    );

    /**
     * Returns a promise that resolves either
     * (1) immediately when isLoading is false
     * (2) as soon as isLoading is false and a new value is loaded
     */
    async function isReady(): Promise<boolean> {
        // Ensure that lazy-loaded values will be read
        await until(isActive).not.toBeNull();
        await until(loading).not.toBeTruthy();
        return isActive.value ?? false;
    }

    return computed<FeatureFlagState>(() => ({
        isActive: isActive.value ?? false,
        loading: loading.value,
        isReady,
        availableInLicenseTiers: null,
    }));
}

function useHierarchicalFeatureFlags() {
    const {featureFlagResponse, isLoading} = useFeatureFlagResponse();

    function useFeatureFlag(flagName: FeatureFlagName): ComputedRef<FeatureFlagState> {
        const featureFlag = computed<LicensedFeatureFlag>(
            () => featureFlagResponse.value[flagName] ?? null
        );

        const isActive = computed<boolean>(() => !isLoading.value && featureFlag.value !== false);
        const availableInLicenseTiers = computed<ValidLicense[] | null>(() => {
            const flag = featureFlag.value;
            if (Array.isArray(flag)) {
                return flag;
            }
            return null;
        });

        async function isReady(): Promise<boolean> {
            await until(isLoading).toBe(false);
            return isActive.value;
        }

        return computed<FeatureFlagState>(() => ({
            isActive: isActive.value ?? false,
            loading: isLoading.value,
            isReady,
            availableInLicenseTiers: availableInLicenseTiers.value,
        }));
    }

    return {
        useFeatureFlag,
    };
}

/**
 * Combines multiple feature flags into one, requiring at least one to be active.
 * The resulting feature flag state will hold the lowest license tier to make any available.
 */
function useCombinedAnyFeatureFlags(...featureFlags: ComputedRef<FeatureFlagState>[]) {
    const isActive = computed(() => featureFlags.some(flag => flag.value.isActive));
    const loading = computed(() => featureFlags.some(flag => flag.value.loading));

    const availableInLicenseTiers = computed<ValidLicense[] | null>(() => {
        const licenseTiers = featureFlags
            .map(flag => flag.value.availableInLicenseTiers)
            .filter((tiers): tiers is ValidLicense[] => tiers !== null);

        if (licenseTiers.length !== featureFlags.length) {
            return null;
        }

        return Array.from(new Set(licenseTiers.flat()));
    });

    return computed<FeatureFlagState>(() => ({
        isActive: isActive.value,
        loading: loading.value,
        isReady: async () => {
            await until(loading).toBe(false);
            return isActive.value;
        },
        availableInLicenseTiers: availableInLicenseTiers.value,
    }));
}

function useFeatureFlagResponse() {
    const {isAuthenticated, user} = storeToRefs(useUserStore());

    const isLoading = ref(true);
    const featureFlagResponse = computedAsync(
        () => {
            if (!isAuthenticated.value || !user.value?.id) {
                return {} as FeatureFlagResponse;
            }
            // TODO Use TanStack query as soon as available
            return API.get('/api/unleash')
                .then(API.handle)
                .then(data => parseZodSchema(data, featureFlagResponseModel))
                .catch(() => ({}) as FeatureFlagResponse);
        },
        {},
        isLoading
    );

    return {featureFlagResponse, isLoading};
}
